import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * User role types
 * Legacy roles (admin, client) are supported but normalized to new format
 */
export type UserRole = 
  | 'admin'      // Legacy, maps to k-admin
  | 'client'     // Legacy, maps to c-client
  | 'service'    // Service accounts
  | 'k-admin'    // Kampus admin
  | 'k-csteam'   // Kampus customer service
  | 'c-owner'    // Client org owner
  | 'c-client'   // Client standard user
  | 'c-reader';  // Client read-only

/**
 * User object stored in auth state
 */
interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  organization_id?: string | null;
  avatar?: string;
}

/**
 * Auth state interface
 */
interface AuthState {
  token: string | null;
  refreshToken: string | null;
  apiKey: string | null;
  user: User | null;
  isAuthenticated: boolean;
  lastVerified: number | null;
  
  // Actions
  setAuth: (token: string, user: User, refreshToken?: string) => void;
  setApiKey: (key: string) => void;
  updateUser: (updates: Partial<User>) => void;
  updateToken: (token: string) => void;
  logout: () => void;
  markVerified: () => void;
}

/**
 * Normalize role for consistency
 */
export function normalizeRole(role: string): UserRole {
  if (role === 'admin') return 'k-admin';
  if (role === 'client') return 'c-client';
  return role as UserRole;
}

/**
 * Check if a role is an admin role
 */
export function isAdminRole(role: UserRole): boolean {
  return role === 'admin' || role === 'k-admin';
}

/**
 * Check if a role is a Kampus staff role
 */
export function isKampusStaff(role: UserRole): boolean {
  const normalized = normalizeRole(role);
  return normalized === 'k-admin' || normalized === 'k-csteam';
}

/**
 * Auth store using Zustand with persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      apiKey: null,
      user: null,
      isAuthenticated: false,
      lastVerified: null,

      setAuth: (token, user, refreshToken) => {
        // Sync token to localStorage for API client
        localStorage.setItem('auth-token', token);
        if (refreshToken) {
          localStorage.setItem('refresh-token', refreshToken);
        }
        set({
          token,
          refreshToken: refreshToken ?? null,
          user,
          isAuthenticated: true,
          lastVerified: Date.now(),
        });
      },

      setApiKey: (apiKey) => set({ apiKey }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      updateToken: (token) => {
        localStorage.setItem('auth-token', token);
        set({ token, lastVerified: Date.now() });
      },

      logout: () => {
        // Clear tokens from localStorage
        localStorage.removeItem('auth-token');
        localStorage.removeItem('refresh-token');
        set({
          token: null,
          refreshToken: null,
          apiKey: null,
          user: null,
          isAuthenticated: false,
          lastVerified: null,
        });
      },

      markVerified: () => set({ lastVerified: Date.now() }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        apiKey: state.apiKey,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastVerified: state.lastVerified,
      }),
    }
  )
);

/**
 * Selector to get current user
 */
export const selectUser = (state: AuthState) => state.user;

/**
 * Selector to check if authenticated
 */
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;

/**
 * Selector to get current token
 */
export const selectToken = (state: AuthState) => state.token;
